import { useMemo, useState } from 'react';
import {
  addDays,
  applyCopy,
  copyCandidates,
  isoWeek,
  planCopy,
  saveTemplate,
  startOfIsoWeek,
  templateCandidates,
  type ISODate,
} from '@autoday/core';
import { getStore, useStore } from '../store/appStore.ts';
import { Badge, Button, Field, Sheet, inputCls } from '../ui/kit.tsx';
import { errorText, toast } from '../ui/toast.tsx';
import { timeLabel } from '../ui/format.ts';
import { randomId } from '../platform.ts';
import { useLang } from '../i18n/i18n.ts';
import { formatDateLong, formatDateShort, weekdayName } from '../i18n/format.ts';
import { Temporal } from '@autoday/core';

export type CopyRequest = { mode: 'day' | 'week'; date: ISODate } | { mode: 'template'; templateId?: string; date: ISODate };

export function CopyDialog({ request, onClose }: { request: CopyRequest; onClose: () => void }) {
  const { state } = useStore();
  const store = getStore();
  const { t } = useLang();
  const tz = state.settings.timeZone;
  const [mode, setMode] = useState<'day' | 'week' | 'template'>(request.mode);
  const [sourceDate, setSourceDate] = useState(request.mode === 'week' ? startOfIsoWeek(request.date) : request.date);
  const [templateId, setTemplateId] = useState<string>(request.mode === 'template' ? request.templateId ?? Object.keys(state.templates)[0] ?? '' : Object.keys(state.templates)[0] ?? '');
  // Ett operations-ID per öppnad dialog → dubbelklick och omförsök skapar aldrig dubbletter.
  const [opId] = useState(() => randomId('cp_'));
  const [targets, setTargets] = useState<ISODate[]>([]);
  const [extra, setExtra] = useState('');
  const [busy, setBusy] = useState(false);
  const [tplName, setTplName] = useState('');

  const span = mode === 'week' ? 7 : mode === 'template' ? (state.templates[templateId]?.span === 'week' ? 7 : 1) : 1;
  const srcStart = mode === 'week' ? startOfIsoWeek(sourceDate) : sourceDate;
  const { candidates, excludedImported } = useMemo(() => {
    if (mode === 'template') {
      const t = state.templates[templateId];
      return { candidates: t ? templateCandidates(t) : [], excludedImported: [] };
    }
    return copyCandidates(state, srcStart, span);
  }, [state, mode, templateId, srcStart, span]);
  const [selected, setSelected] = useState<Set<number> | null>(null);
  const sel = selected ?? new Set(candidates.map((_, i) => i));

  const suggestions: ISODate[] = useMemo(() => {
    const out: ISODate[] = [];
    if (span === 7) for (let w = 1; w <= 6; w++) out.push(addDays(startOfIsoWeek(srcStart), 7 * w));
    else for (let d = 1; d <= 14; d++) out.push(addDays(srcStart, d));
    return out;
  }, [span, srcStart]);

  const plan = useMemo(() => planCopy(state, opId, candidates, [...sel], targets, store.clock.now()), [state, opId, candidates, sel, targets, store]);

  const toggleTarget = (d: ISODate) => setTargets((t) => (t.includes(d) ? t.filter((x) => x !== d) : [...t, d].sort()));

  async function apply() {
    if (busy || !plan.items.length) return;
    setBusy(true);
    try {
      const r = await store.run((s) => applyCopy(s, plan, store.clock.now(), `Kopiera ${mode === 'week' ? 'vecka' : mode === 'template' ? 'mall' : 'dag'} (${plan.items.length} st)`));
      if (r.duplicate) toast({ kind: 'info', text: t('Kopieringen var redan genomförd – inga dubbletter skapades.', 'The copy had already been made – no duplicates were created.') });
      else
        toast({
          kind: 'ok',
          text: t(`${plan.items.length} kopior skapade.`, `${plan.items.length} copies created.`),
          action: r.undo ? { label: t('Ångra allt', 'Undo all'), run: () => void store.undo(r.undo!.opId).then(() => toast({ kind: 'info', text: t('Kopieringen ångrad.', 'The copy was undone.') })).catch((e) => toast({ kind: 'error', text: errorText(e) })) } : undefined,
        });
      onClose();
    } catch (e) {
      toast({ kind: 'error', text: `${t('Kopieringen genomfördes inte.', 'The copy was not made.')} ${errorText(e)}` });
    } finally {
      setBusy(false);
    }
  }

  async function saveTpl() {
    try {
      await store.run((s) => saveTemplate(s, randomId('tpl_'), tplName, mode === 'week' ? 'week' : 'day', srcStart, store.clock.now()));
      toast({ kind: 'ok', text: t('Mallen sparad.', 'Template saved.') });
      setTplName('');
    } catch (e) {
      toast({ kind: 'error', text: errorText(e) });
    }
  }

  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      wide
      title={mode === 'week' ? t('Kopiera vecka', 'Copy week') : mode === 'template' ? t('Använd mall', 'Use template') : t('Kopiera dag', 'Copy day')}
      description={t('Befintliga aktiviteter på måldagarna ersätts aldrig. Importerade deadlines kopieras inte.', 'Existing activities on the target days are never replaced. Imported deadlines are not copied.')}
      footer={
        <>
          <Button onClick={onClose}>{t('Avbryt', 'Cancel')}</Button>
          <Button variant="primary" disabled={busy || !plan.items.length} onClick={() => void apply()}>
            {busy ? t('Kopierar…', 'Copying…') : t(`Skapa ${plan.items.length} kopi${plan.items.length === 1 ? 'a' : 'or'}`, `Create ${plan.items.length} cop${plan.items.length === 1 ? 'y' : 'ies'}`)}
          </Button>
        </>
      }
    >
      <div className="grid gap-5 text-sm">
        <div role="radiogroup" aria-label={t('Typ', 'Type')} className="flex flex-wrap gap-1.5">
          {(['day', 'week', 'template'] as const).map((m) => (
            <button key={m} role="radio" aria-checked={mode === m} onClick={() => { setMode(m); setTargets([]); setSelected(null); }} className={`min-h-11 rounded-lg border px-3 ${mode === m ? 'border-accent bg-raised' : 'border-border-strong text-muted'}`}>
              {{ day: t('Dag', 'Day'), week: t('Vecka', 'Week'), template: t('Mall', 'Template') }[m]}
            </button>
          ))}
        </div>

        {mode !== 'template' ? (
          <Field label={mode === 'week' ? t('Källvecka (välj en dag i veckan)', 'Source week (pick a day in the week)') : t('Källdag', 'Source day')} htmlFor="cp-src">
            <input id="cp-src" type="date" className={inputCls} value={sourceDate} onChange={(e) => { setSourceDate(e.target.value); setSelected(null); setTargets([]); }} />
          </Field>
        ) : Object.keys(state.templates).length ? (
          <Field label={t('Mall', 'Template')} htmlFor="cp-tpl">
            <select id="cp-tpl" className={inputCls} value={templateId} onChange={(e) => { setTemplateId(e.target.value); setSelected(null); }}>
              {Object.values(state.templates).map((tp) => (
                <option key={tp.id} value={tp.id}>
                  {tp.name} ({tp.span === 'week' ? t('vecka', 'week') : t('dag', 'day')}, {tp.items.length})
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <p className="text-muted">{t('Inga sparade mallar ännu. Kopiera en dag eller vecka och spara den som mall.', 'No saved templates yet. Copy a day or week and save it as a template.')}</p>
        )}

        <fieldset>
          <legend className="mb-1 font-medium">{t(`Aktiviteter (${sel.size} av ${candidates.length} valda)`, `Activities (${sel.size} of ${candidates.length} selected)`)}</legend>
          {candidates.length ? (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {candidates.map((c, i) => (
                <li key={i}>
                  <label className="flex min-h-11 items-center gap-3 px-3 py-2">
                    <input type="checkbox" className="h-5 w-5" checked={sel.has(i)} onChange={() => { const n = new Set(sel); if (n.has(i)) n.delete(i); else n.add(i); setSelected(n); }} />
                    <span className="flex-1">{c.title}</span>
                    <span className="tabular-nums text-muted">{span === 7 ? formatDateShort(addDays(srcStart, c.dayOffset)) + ' ' : ''}{timeLabel(c.timing, tz)}</span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted">{mode === 'week' ? t('Inga egna aktiviteter den veckan.', 'No activities of your own that week.') : t('Inga egna aktiviteter den dagen.', 'No activities of your own that day.')}</p>
          )}
          {excludedImported.length > 0 && (
            <p className="mt-2 text-muted">
              {t(`${excludedImported.length} importerad${excludedImported.length > 1 ? 'e' : ''} deadline${excludedImported.length > 1 ? 's' : ''} exkluderas:`, `${excludedImported.length} imported deadline${excludedImported.length > 1 ? 's are' : ' is'} excluded:`)}{' '}
              {excludedImported.map((e) => e.title).join('; ')}
            </p>
          )}
        </fieldset>

        <fieldset>
          <legend className="mb-1 font-medium">
            {span === 7 ? t(`Till veckor (${targets.length} valda)`, `To weeks (${targets.length} selected)`) : t(`Till dagar (${targets.length} valda)`, `To days (${targets.length} selected)`)}
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {[...new Set([...suggestions, ...targets])].sort().map((d) => (
              <button key={d} aria-pressed={targets.includes(d)} onClick={() => toggleTarget(d)} className={`min-h-11 rounded-lg border px-3 ${targets.includes(d) ? 'border-accent bg-raised text-text' : 'border-border-strong text-muted'}`}>
                {span === 7 ? `${t('v.', 'wk')} ${isoWeek(d).week}` : t(formatDateLong(d, false).replace(/^(\S{3})\S*/, '$1'), `${weekdayName(Temporal.PlainDate.from(d).dayOfWeek, true)} ${formatDateShort(d)}`)}
              </button>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <label className="sr-only" htmlFor="cp-extra">
              {t('Lägg till datum', 'Add date')}
            </label>
            <input id="cp-extra" type="date" className={inputCls} value={extra} onChange={(e) => setExtra(e.target.value)} />
            <Button disabled={!extra} onClick={() => { const d = span === 7 ? startOfIsoWeek(extra) : extra; if (!targets.includes(d)) toggleTarget(d); setExtra(''); }}>{t('Lägg till', 'Add')}</Button>
          </div>
        </fieldset>

        {targets.length > 0 && (
          <section aria-live="polite" className="rounded-lg border border-border bg-raised p-3">
            <h3 className="font-semibold">{t('Förhandsvisning', 'Preview')}</h3>
            <p className="mt-1">
              {t(
                `${plan.items.length} kopior · ${plan.conflictCount} med krock${excludedImported.length ? ` · ${excludedImported.length} importerade deadlines exkluderade` : ''}`,
                `${plan.items.length} copies · ${plan.conflictCount} with a clash${excludedImported.length ? ` · ${excludedImported.length} imported deadlines excluded` : ''}`,
              )}
            </p>
            <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
              {plan.items.map((it) => (
                <li key={it.newEvent.id} className="flex flex-wrap items-center gap-2">
                  <span className="tabular-nums text-muted">{formatDateShort(it.newEvent.timing.kind === 'allDay' ? it.newEvent.timing.start : it.newEvent.timing.start.slice(0, 10))} {timeLabel(it.newEvent.timing, tz)}</span>
                  <span>{it.newEvent.title}</span>
                  {it.conflicts.length > 0 && (
                    <Badge tone="warn">
                      {t('Krockar med', 'Clashes with')} {it.conflicts.map((c) => c.title).join(', ')}
                    </Badge>
                  )}
                  {it.adjusted && <Badge tone="info">{t('Sommartidsjusterad', 'Adjusted for DST')}</Badge>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {mode !== 'template' && candidates.length > 0 && (
          <div className="flex flex-wrap items-end gap-2 border-t border-border pt-4">
            <Field label={mode === 'week' ? t('Spara veckan som mall', 'Save the week as a template') : t('Spara dagen som mall', 'Save the day as a template')} htmlFor="cp-tplname">
              <input id="cp-tplname" className={inputCls} value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder={mode === 'week' ? t('Normalvecka', 'Normal week') : t('Arbetsdag', 'Work day')} />
            </Field>
            <Button onClick={() => void saveTpl()}>{t('Spara mall', 'Save template')}</Button>
          </div>
        )}
      </div>
    </Sheet>
  );
}
